import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import {
  isAuthError,
  isForbiddenError,
  isInvalidError,
  isSuccess,
} from '@/context/services/type';
import { TransactionItemDetailOptionsColumn } from '@/features/transaction_items/Detail/type';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { useQueryClient } from '@tanstack/react-query';
import {
  ApGeneralInputOption,
  ApInvoiceTransactionGeneralInput,
  CommonError,
  GetApInvoiceTransactionGeneralInputOptionsParams,
  getGetApInvoiceTransactionGeneralInputOptionsQueryKey,
  UnauthorizedResponse,
  useDeleteApGeneralInputOptions,
  useGetApInvoiceTransactionGeneralInputOptionHook,
  useGetApInvoiceTransactionGeneralInputOptionsHook,
  useGetApInvoiceTransactionGeneralInputOptionsSuspense,
  useGetApInvoiceTransactionGeneralInputSuspense,
  usePostApGeneralInputOptions,
  usePutApGeneralInputOptions,
} from 'ap-openapi';
import { useCallback } from 'react';

type FetchTransactionItemsDetail = (id: string) => {
  data: ApInvoiceTransactionGeneralInput | undefined;
};

export type FetchTransactionItemDetailOptions = (
  id: string,
  params: GetApInvoiceTransactionGeneralInputOptionsParams
) => {
  data: TransactionItemDetailOptionsColumn[];
  pagination: ModernPaginationProps;
};

export type TransactionItemOptionsQuery =
  GetApInvoiceTransactionGeneralInputOptionsParams;

export const useGetApInvoiceTransactionGeneralInputOptions = () => {
  return useGetApInvoiceTransactionGeneralInputOptionsHook();
};

export const useGetTransactionItemDetail: FetchTransactionItemsDetail = (
  id
) => {
  const { data } = useGetApInvoiceTransactionGeneralInputSuspense(id);

  if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
    location.href = data.headers?.location ?? '';
  }

  return {
    data: data?.data?.ap_invoice_transaction_general_input,
  };
};

export const useGetTransactionItemDetailOptions: FetchTransactionItemDetailOptions =
  (id: string, params: GetApInvoiceTransactionGeneralInputOptionsParams) => {
    const { data } = useGetApInvoiceTransactionGeneralInputOptionsSuspense(
      id,
      params
    );

    if (data && (isAuthError(data.status) || isForbiddenError(data.status))) {
      location.href = data.headers?.location ?? '';
    }

    const pagination: ModernPaginationProps = useConvertToModernPagination(
      data?.data?.pagination
    );

    return {
      data: data.data.ap_general_input_options?.map(responseToColumn) ?? [],
      pagination: pagination,
    };
  };

const responseToColumn = (
  item: ApGeneralInputOption
): TransactionItemDetailOptionsColumn => {
  return {
    id: item.id?.toString() ?? '',
    code: item.code ?? '',
    label: item.label ?? '',
    priority: item.priority ?? 0,
    is_visible: item.is_visible ?? false,
  };
};

export const useGetTransactionItemPulldownOption = () => {
  return useGetApInvoiceTransactionGeneralInputOptionHook();
};

export enum TransactionItemDetailOptionStatus {
  SUCCESS = 'success',
  INVALID = 'invalid',
  AUTH = 'auth',
  ERROR = 'error',
}

// Common types for all operations
export type ValidationErrorResponse = {
  errors: {
    [key: string]: {
      message: string;
      code: string;
    }[];
  };
};

type BaseOperationSuccess<T = unknown> = {
  status: TransactionItemDetailOptionStatus.SUCCESS;
  data: T;
};

type BaseOperationInvalidError = {
  status: TransactionItemDetailOptionStatus.INVALID;
  errors: ValidationErrorResponse;
};

type BaseOperationAuthError = {
  status: TransactionItemDetailOptionStatus.AUTH;
  errors: UnauthorizedResponse;
  redirectPath: string;
};

type BaseOperationCommonError = {
  status: TransactionItemDetailOptionStatus.ERROR;
  errors: CommonError;
};

type OperationResult<T = unknown> =
  | BaseOperationSuccess<T>
  | BaseOperationInvalidError
  | BaseOperationAuthError
  | BaseOperationCommonError;

// Specific operation result types
type CreateResult = OperationResult<ApGeneralInputOption>;
type UpdateResult = OperationResult<ApGeneralInputOption>;
type DeleteResult =
  | (Omit<BaseOperationSuccess<never>, 'data'> & {
      status: TransactionItemDetailOptionStatus.SUCCESS;
    })
  | BaseOperationAuthError
  | BaseOperationCommonError;

const handleOperationResponse = <T>(
  resp: {
    status: number;
    data?: { ap_general_input_option?: T } & Record<string, unknown>;
    headers?: { location?: string };
  },
  includeInvalidError = true
): OperationResult<T> => {
  if (isSuccess(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.SUCCESS,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      data: resp.data?.ap_general_input_option ?? ({} as T),
    };
  } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.AUTH,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as UnauthorizedResponse,
      redirectPath: resp.headers?.location ?? '/',
    };
  } else if (includeInvalidError && isInvalidError(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.INVALID,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as ValidationErrorResponse,
    };
  } else {
    return {
      status: TransactionItemDetailOptionStatus.ERROR,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as CommonError,
    };
  }
};

const handleDeleteResponse = (resp: {
  status: number;
  data?: unknown;
  headers?: { location?: string };
}): DeleteResult => {
  if (isSuccess(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.SUCCESS,
    };
  } else if (isAuthError(resp.status) || isForbiddenError(resp.status)) {
    return {
      status: TransactionItemDetailOptionStatus.AUTH,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as UnauthorizedResponse,
      redirectPath: resp.headers?.location ?? '/',
    };
  } else {
    return {
      status: TransactionItemDetailOptionStatus.ERROR,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      errors: (resp.data ?? {}) as CommonError,
    };
  }
};

const useDataMutation = <TMutationArgs, TUserArgs, TResult>(
  apInvoiceTransactionGeneralInputId: string,
  mutationHook: (options?: {
    mutation?: {
      onSuccess?: () => void;
    };
  }) => {
    mutateAsync: (
      args: TMutationArgs
    ) => Promise<{ status: number; data?: Record<string, unknown> }>;
    isPending?: boolean;
  },
  argsTransformer: (userArgs: TUserArgs) => TMutationArgs,
  responseHandler: (resp: {
    status: number;
    data?: Record<string, unknown>;
  }) => TResult
) => {
  const query = useQueryClient();
  const mutationResult = mutationHook();

  const mutateAsync = useCallback(
    async (args: TUserArgs): Promise<TResult> => {
      const transformedArgs = argsTransformer(args);
      const resp = await mutationResult.mutateAsync(transformedArgs);
      const result = responseHandler(resp);

      // Only invalidate queries on actual success
      if (isSuccess(resp.status)) {
        query.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputOptionsQueryKey(
            apInvoiceTransactionGeneralInputId
          ),
        });
      }

      return result;
    },
    [
      mutationResult,
      responseHandler,
      argsTransformer,
      query,
      apInvoiceTransactionGeneralInputId,
    ]
  );

  return { mutateAsync };
};

const useDeleteMutation = <TMutationArgs, TUserArgs, TResult>(
  id: string,
  mutationHook: (options?: {
    mutation?: {
      onSuccess?: () => void;
    };
  }) => {
    mutateAsync: (
      args: TMutationArgs
    ) => Promise<{ status: number; data?: unknown }>;
    isPending?: boolean;
  },
  argsTransformer: (userArgs: TUserArgs) => TMutationArgs,
  responseHandler: (resp: { status: number; data?: unknown }) => TResult
) => {
  const query = useQueryClient();
  const mutationResult = mutationHook();

  const mutateAsync = useCallback(
    async (args: TUserArgs): Promise<TResult> => {
      const transformedArgs = argsTransformer(args);
      const resp = await mutationResult.mutateAsync(transformedArgs);
      const result = responseHandler(resp);

      // Only invalidate queries on actual success
      if (isSuccess(resp.status)) {
        query.invalidateQueries({
          queryKey: getGetApInvoiceTransactionGeneralInputOptionsQueryKey(id),
        });
      }

      return result;
    },
    [mutationResult, responseHandler, argsTransformer, query, id]
  );

  return {
    mutateAsync,
    isLoading: mutationResult.isPending ?? false,
  };
};

type CreateTransactionItemDetailOptionMutateArgs = Parameters<
  ReturnType<typeof usePostApGeneralInputOptions>['mutateAsync']
>[0];

export type CreateTransactionItemDetailOptionUnprocessableEntityResponse =
  ValidationErrorResponse;

export const useCreateTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDataMutation<
    CreateTransactionItemDetailOptionMutateArgs,
    {
      data: {
        code: string;
        label: string;
        priority: number;
        is_visible: boolean;
      };
    },
    CreateResult
  >(
    apInvoiceTransactionGeneralInputId,
    usePostApGeneralInputOptions,
    (args) => ({
      id: apInvoiceTransactionGeneralInputId,
      data: {
        code: args.data.code ?? '',
        label: args.data.label ?? '',
        priority: args.data.priority ?? 0,
        is_visible: args.data.is_visible ?? false,
      },
    }),
    (resp) => handleOperationResponse<ApGeneralInputOption>(resp)
  );
};

type UpdateTransactionItemDetailOptionMutateArgs = Parameters<
  ReturnType<typeof usePutApGeneralInputOptions>['mutateAsync']
>[0];

// Maintain backward compatibility for exported types
export type UpdateTransactionItemDetailOptionUnprocessableEntityResponse =
  ValidationErrorResponse;

export const useUpdateTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDataMutation<
    UpdateTransactionItemDetailOptionMutateArgs,
    {
      id: string;
      data: {
        code: string;
        label: string;
        priority: number;
        is_visible: boolean;
      };
    },
    UpdateResult
  >(
    apInvoiceTransactionGeneralInputId,
    usePutApGeneralInputOptions,
    (args) => ({
      apInvoiceTransactionGeneralInputId,
      id: args.id,
      data: {
        code: args.data.code ?? '',
        label: args.data.label ?? '',
        priority: args.data.priority ?? 0,
        is_visible: args.data.is_visible ?? false,
      },
    }),
    (resp) => handleOperationResponse<ApGeneralInputOption>(resp)
  );
};

export const useDeleteTransactionItemDetailOption = (
  apInvoiceTransactionGeneralInputId: string
) => {
  return useDeleteMutation<
    { apInvoiceTransactionGeneralInputId: string; id: string },
    string,
    DeleteResult
  >(
    apInvoiceTransactionGeneralInputId,
    useDeleteApGeneralInputOptions,
    (id) => ({
      apInvoiceTransactionGeneralInputId,
      id,
    }),
    handleDeleteResponse
  );
};
